"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useId, useState } from "react";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWater } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import { TOOLTIP_STYLES } from "@/helpers/tooltip.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
interface WaveCreatorBadgeProps {
  readonly tooltipId?: string | undefined;
  readonly onBadgeClick?: (() => void) | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly waveName?: string | null | undefined;
  readonly wavePfp?: string | null | undefined;
  readonly showWaveDetails?: boolean | undefined;
}
const getTrimmedText = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
};
export const WaveCreatorBadge: React.FC<WaveCreatorBadgeProps> = t0 => {
  const $ = _c(33);
  const {
    tooltipId: t1,
    onBadgeClick,
    size: t2,
    waveName,
    wavePfp,
    showWaveDetails: t3
  } = t0;
  const tooltipId = t1 === undefined ? "wave-creator-badge" : t1;
  const size = t2 === undefined ? "default" : t2;
  const showWaveDetails = t3 === undefined ? false : t3;
  const isMobile = useIsMobileDevice();
  const id = useId();
  const uniqueTooltipId = `${tooltipId}-${id}`;
  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);
  const [failedImageSrc, setFailedImageSrc] = useState(null);
  const showTooltip = isMobile === false;
  const dataTooltipId = showTooltip ? uniqueTooltipId : undefined;
  let t4;
  if ($[0] !== waveName) {
    t4 = getTrimmedText(waveName);
    $[0] = waveName;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const normalizedWaveName = t4;
  let t5;
  if ($[2] !== wavePfp) {
    const normalizedWavePfp = getTrimmedText(wavePfp);
    t5 = normalizedWavePfp ? getScaledImageUri(normalizedWavePfp, ImageScale.W_AUTO_H_50) : null;
    $[2] = wavePfp;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const waveImageSrc = t5;
  const shouldShowWaveDetails = showWaveDetails && size !== "compact";
  const displayWaveName = normalizedWaveName ?? "profile wave";
  const tooltipContent = shouldShowWaveDetails ? displayWaveName : "View created waves";
  const ariaLabel = shouldShowWaveDetails ? `Open ${displayWaveName}` : "View created waves";
  const buttonSizeClassName = size === "compact" ? "tw-h-[18px] tw-w-[18px]" : "tw-h-5 tw-w-5";
  const iconSizeClassName = size === "compact" ? "tw-h-[9px] tw-w-[9px]" : "tw-h-2.5 tw-w-2.5";
  const buttonClassName = shouldShowWaveDetails ? `tw-inline-flex ${buttonSizeClassName} tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border tw-border-solid tw-border-white/15 tw-bg-white/5 tw-text-iron-200 tw-outline-none tw-ring-0 tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/20 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950 desktop-hover:hover:tw-border-white/25 desktop-hover:hover:tw-bg-white/10 desktop-hover:hover:tw-text-iron-100` : `tw-inline-flex ${buttonSizeClassName} tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-white/15 tw-bg-white/5 tw-text-white/60 tw-outline-none tw-ring-0 tw-transition-colors tw-duration-200 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/20 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-iron-950 desktop-hover:hover:tw-border-white/20 desktop-hover:hover:tw-bg-white/10 desktop-hover:hover:tw-text-white/70`;
  let t6;
  bb0: {
    if (!shouldShowWaveDetails) {
      const t7 = `${iconSizeClassName} tw-flex-shrink-0 tw-text-current`;
      let t8;
      if ($[4] !== t7) {
        t8 = <FontAwesomeIcon icon={faWater} className={t7} />;
        $[4] = t7;
        $[5] = t8;
      } else {
        t8 = $[5];
      }
      t6 = t8;
      break bb0;
    }
    const imageSrc = waveImageSrc;
    if (!imageSrc || failedImageSrc === imageSrc) {
      const t7 = `${iconSizeClassName} tw-flex-shrink-0 tw-text-current`;
      let t8;
      if ($[6] !== t7) {
        t8 = <FontAwesomeIcon icon={faWater} className={t7} aria-hidden="true" />;
        $[6] = t7;
        $[7] = t8;
      } else {
        t8 = $[7];
      }
      t6 = t8;
      break bb0;
    }
    let t7;
    if ($[8] !== imageSrc) {
      t7 = () => setFailedImageSrc(imageSrc);
      $[8] = imageSrc;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== imageSrc || $[11] !== t7) {
      t8 = <Image src={imageSrc} alt="" width={20} height={20} unoptimized={true} className="tw-block tw-object-cover" onError={t7} />;
      $[10] = imageSrc;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t6 = t8;
  }
  const badgeContent = t6;
  let t7;
  if ($[13] !== onBadgeClick) {
    t7 = e => {
      e.preventDefault();
      e.stopPropagation();
      setIsTooltipOpen(false);
      onBadgeClick?.();
    };
    $[13] = onBadgeClick;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsTooltipOpen(true);
    t9 = () => setIsTooltipOpen(false);
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== dataTooltipId) {
    t10 = dataTooltipId && {
      "data-tooltip-id": dataTooltipId
    };
    $[17] = dataTooltipId;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== ariaLabel || $[20] !== badgeContent || $[21] !== buttonClassName || $[22] !== t10 || $[23] !== t7) {
    t11 = <button type="button" onClick={t7} onMouseEnter={t8} onMouseLeave={t9} className={buttonClassName} aria-label={ariaLabel} {...t10}>{badgeContent}</button>;
    $[19] = ariaLabel;
    $[20] = badgeContent;
    $[21] = buttonClassName;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== isTooltipOpen || $[26] !== showTooltip || $[27] !== tooltipContent || $[28] !== uniqueTooltipId) {
    t12 = showTooltip && <Tooltip id={uniqueTooltipId} place="top" positionStrategy="fixed" offset={8} opacity={1} style={TOOLTIP_STYLES} isOpen={isTooltipOpen}><span className="tw-text-xs">{tooltipContent}</span></Tooltip>;
    $[25] = isTooltipOpen;
    $[26] = showTooltip;
    $[27] = tooltipContent;
    $[28] = uniqueTooltipId;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
};